// CARO. Casa de estilo — i18n provider (ES + EN + FR).
// The locale is URL-based: ES at the root, EN under /en/..., FR under /fr/...
// (see i18n/locale.ts).
// It's fixed for the page load; switching locale is a full navigation to the
// other language's URL (so the edge serves the right server-rendered <head> and
// React Router's basename matches). Exposes t(key) and <html lang>.

import { createContext, useCallback, useContext, useEffect, useMemo } from 'react';
import type { ReactNode } from 'react';
import { translate, type UILocale } from '@/client/i18n/dictionary';
import { INITIAL_LOCALE, localeSwitchUrl } from '@/client/i18n/locale';

interface I18nValue {
  locale: UILocale;
  setLocale: (l: UILocale) => void;
  t: (key: string) => string;
}

const I18nContext = createContext<I18nValue | null>(null);

export function I18nProvider({ children }: { children: ReactNode }) {
  const locale = INITIAL_LOCALE;

  useEffect(() => {
    if (typeof document !== 'undefined') document.documentElement.lang = locale;
  }, [locale]);

  // Switching locale navigates (full load) to the same page in the other
  // language; the URL is the source of truth, so there's no in-place state.
  const setLocale = useCallback((l: UILocale) => {
    if (l === locale) return;
    window.location.assign(localeSwitchUrl(l));
  }, [locale]);

  const t = useCallback((key: string) => translate(locale, key), [locale]);

  const value = useMemo<I18nValue>(() => ({ locale, setLocale, t }), [locale, setLocale, t]);
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nValue {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error('useI18n must be used within I18nProvider');
  return ctx;
}
